import type { HostLike, Surface } from './types'

/**
 * Tauri v2 往 window 上注入的内部对象。
 *
 * 名字是 v2 的（v1 叫 `__TAURI__`）。壳升级到 v3 时这里会认不出来，
 * 表现是**本机能力在客户端里退回 Web 那一档**——那是安全的失败方向：
 * 少一条原生路是功能缺失，多一条走不通的路是骗人。两种都得修，
 * 但前者不会让调用方以为自己拿到了原生能力。
 */
const TAURI_INTERNALS = '__TAURI_INTERNALS__'

/**
 * 这份产物此刻跑在哪儿。
 *
 * # 判据为什么是运行时问一句，而不是 UA 或者构建时开关
 *
 * UA 认的是「什么浏览器」，而 macOS 客户端里跑的也是 WebKit——
 * 一台 Mac 上的 Safari 会和客户端认成同一个东西。
 *
 * 构建时开关（`import.meta.env`）能分开，但它分的是「这份产物是给谁编的」，
 * 而 `tauri.conf.json` 的 `frontendDist` 指的就是 `apps/console/dist`：
 * **Web 与 macOS 用的是同一份产物**，构建时根本没有两份可分。
 *
 * 剩下的唯一真判据是问一句「我这个宿主里有没有原生那一侧注入的东西」。
 *
 * # 与 `apps/console/src/screens/lulu/platform.ts` 的关系
 *
 * 那一份是这一份的前身，判据一模一样。**它现在还在**——lulu 屏这一轮
 * 归别的流，不能动。`apps/console/src/app/platform-boundary.test.ts` 把它
 * 记成一条已知违规，并且**那条记录会自己到期**：等它迁过来、不再直接摸
 * `__TAURI_INTERNALS__` 的那一天，那条基线匹配不上，用例当场红，
 * 提示把它删掉。写在注释里的「记得迁」挡不住任何事，这一条挡得住。
 */
export function detectSurface(host: unknown = globalThis): Surface {
  if (typeof host !== 'object' || host === null) return 'web'
  return TAURI_INTERNALS in (host as HostLike) ? 'macos' : 'web'
}

/*
 * 这里刻意**没有** `tauriInternals()` / `invoke()` 那样的调用口。
 *
 * 这一轮进包的唯一能力（剪贴板）不需要调任何 Rust command，写一个出来就是
 * 「抽出来了但没人用」——CLAUDE.md 的判据是「一个共享包如果没有任何消费方
 * 引用它，它就不是『抽出来了』，是『多了一份』」，它对包里的函数一样成立。
 *
 * 第一个真要调 command 的能力（钥匙串最有可能）落地时，由那个人写它，
 * 照着 `apps/console/src/screens/lulu/folder-adapter.ts` 现成那一份。
 * `shell-capabilities.test.ts` 会在那一刻要求他把命令从「还没进接缝」
 * 那张表里挪走。
 */
